import { Dispatch, SetStateAction, useState } from 'react';

import { Button, Center, Flex, Spinner } from '@chakra-ui/react';

import { ColumnDef, PaginationState } from '@tanstack/react-table';

import {
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  PlayCircleOutline,
} from '@aslanpay/portal-common-lib';

import RefreshButton from 'app/components/Reconciliations/RefreshButton';
import RunReconciliationDialog from 'app/components/Reconciliations/RunReconciliationDialog';
import EmptyState from 'app/components/UI/EmptyState';
import { Table } from 'app/components/UI/Table/Table';

import { ReconciliationRunStatusValues } from 'app/constants/reconciliations';

import { ListPage, ReconciliationRunStatus } from 'app/types/reconciliations';

import type { Filter } from '@aslanpay/portal-common-lib';

const PAGE_SIZE_OPTIONS = [
  { value: 15, text: '15 rows / page' },
  { value: 25, text: '25 rows / page' },
  { value: 50, text: '50 rows / page' },
];

interface ReconciliationRunsListProps<T extends { id: string }> {
  runs?: ListPage<T>;
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
  statusFilter: ReconciliationRunStatus[];
  onStatusFilterChange: (statuses: ReconciliationRunStatus[]) => void;
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  companyId?: string;
  companyName?: string;
  canRun: boolean;
  isQueueing: boolean;
  onRun: (companyIds: string[], onSettled: () => void) => void;
  describeRun?: (scope: string) => string;
  testIdPrefix: string;
}

/**
 * Lists reconciliation runs, newest first, with a status filter, a refresh button and a confirmed "Run" action for
 * every company or for the company being managed.
 */
function ReconciliationRunsList<T extends { id: string }>({
  runs = undefined,
  isLoading,
  isRefreshing,
  onRefresh,
  statusFilter,
  onStatusFilterChange,
  pagination,
  onPaginationChange,
  columns,
  companyId = undefined,
  companyName = undefined,
  canRun,
  isQueueing,
  onRun,
  describeRun = undefined,
  testIdPrefix,
}: ReconciliationRunsListProps<T>) {
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false);

  const statusOptions: DropdownOption[] = Object.entries(ReconciliationRunStatusValues).map(
    ([value, { value: text }]) => ({ value, text })
  );
  const filters: Filter[] = [
    { name: 'Status', type: 'category', data: statusOptions, selectedValues: statusFilter },
  ];

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) =>
    (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
    String(filterValue);

  const handleConfirmRun = () =>
    onRun(companyId ? [companyId] : [], () => setIsRunDialogOpen(false));

  const runCount = runs?.list.length ?? 0;

  return (
    <Flex flexDir="column" gap="20px" w="full">
      <Flex align="center" justify="space-between" gap="20px">
        <Filters
          activeFilter={activeFilter}
          displayFormatter={formatFilterValue}
          filters={filters}
          onFilterClose={() => setActiveFilter(undefined)}
          onFilterChange={(_, values) => onStatusFilterChange(values as ReconciliationRunStatus[])}
          onFilterClick={setActiveFilter}
        />
        <Flex gap="12px" flexShrink={0}>
          <RefreshButton
            isRefreshing={isRefreshing}
            onRefresh={onRefresh}
            testId={`refresh-${testIdPrefix}s-button`}
          />
          <Button
            leftIcon={<PlayCircleOutline boxSize="20px" />}
            size="sm"
            flexShrink={0}
            isDisabled={!canRun}
            onClick={() => setIsRunDialogOpen(true)}
            data-testid={`run-${testIdPrefix}-button`}
          >
            {companyId ? 'Run for this company' : 'Run for all companies'}
          </Button>
        </Flex>
      </Flex>

      {isLoading && (
        <Center w="full" py="40px" data-testid={`${testIdPrefix}s-loading`}>
          <Spinner color="brand.purple" size="xl" />
        </Center>
      )}

      {!isLoading && runCount === 0 && (
        <EmptyState
          text={
            statusFilter.length > 0
              ? 'No reconciliations match this filter'
              : 'No reconciliations have run yet'
          }
        />
      )}

      {!isLoading && runCount > 0 && (
        <Table
          columns={columns}
          data={runs?.list || []}
          total={runs?.total || 0}
          pageNumber={runs?.pageNumber ?? pagination.pageIndex}
          pageSize={runs?.pageSize || pagination.pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPaginationChange={onPaginationChange}
          getRowId={(run) => run.id}
        />
      )}

      <RunReconciliationDialog
        isOpen={isRunDialogOpen}
        isQueueing={isQueueing}
        companyName={companyId ? companyName : undefined}
        description={describeRun}
        onClose={() => setIsRunDialogOpen(false)}
        onConfirm={handleConfirmRun}
      />
    </Flex>
  );
}

export default ReconciliationRunsList;
